import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { bffRequestHeaders } from '../services/bffFetch';
import {
  fetchWorkspaceSubscriptionForCustomer,
  parseWorkspaceContextFromBffMePayload,
} from '../services/workspaceSubscription';
import type { WorkspaceSubscription } from '../services/workspaceSubscriptionTypes';

export interface SignInState {
  customerInfo: {
    CustomerID: number;
    TenantID: string;
    CompanyName: string;
    TenantInt: number;
    IsActive: boolean;
    CreationDate: string;
    BasecampWorkspaceId: number | null;
  }[];
  userInfo: {
    IdentityUserID: number;
    UserName: string;
    Email: string;
    FirstName: string | null;
    LastName: string | null;
    DisplayName: string | null;
    PhoneNumber: string | null;
    ThemePreference: string;
  } | null;
  selectedWorkspaceId: number | null;
  /** Basecamp role on the resolved workspace; 0 when unknown or super-admin path. */
  workspaceRoleId: number;
  workspaceSubscription: WorkspaceSubscription | null;
  isWorkspaceSuperAdmin: boolean;
  isAuthed: boolean;
  isVerified: boolean;
  hasAccount: boolean;
  currentCustomerID: number;
  isLoading: boolean;
}

interface SignInStateContextType {
  signInState: SignInState;
  setSignInState: React.Dispatch<React.SetStateAction<SignInState>>;
  reloadWorkspaceForCustomer: (
    customerId: number,
    customers: SignInState['customerInfo']
  ) => Promise<void>;
}

export const SignInStateContext = createContext<SignInStateContextType | null>(null);

function readArray<T>(data: Record<string, unknown>, key: string): T[] {
  const v = data[key];
  return Array.isArray(v) ? (v as T[]) : [];
}

function readOptionalNumber(data: Record<string, unknown>, key: string): number | null {
  const v = data[key];
  if (v === null || v === undefined)
    return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mapCustomerInfoRow(raw: unknown): SignInState['customerInfo'][0] {
  const r = raw as Record<string, unknown>;
  return {
    CustomerID: Number(r.CustomerID ?? 0),
    TenantID: String(r.TenantID ?? ''),
    CompanyName: String(r.CompanyName ?? ''),
    TenantInt: Number(r.TenantInt ?? 0),
    IsActive: Boolean(r.IsActive ?? false),
    CreationDate: String(r.CreationDate ?? ''),
    BasecampWorkspaceId: readOptionalNumber(r, 'BasecampWorkspaceId'),
  };
}

function mapUserInfo(raw: unknown): SignInState['userInfo'] {
  if (!raw || typeof raw !== 'object')
    return null;
  const r = raw as Record<string, unknown>;
  return {
    IdentityUserID: Number(r.IdentityUserID ?? 0),
    UserName: String(r.UserName ?? ''),
    Email: String(r.Email ?? ''),
    FirstName: (r.FirstName as string) ?? null,
    LastName: (r.LastName as string) ?? null,
    DisplayName: (r.DisplayName as string) ?? null,
    PhoneNumber: (r.PhoneNumber as string) ?? null,
    ThemePreference: String(r.ThemePreference ?? 'dark'),
  };
}

function mapBffMeJsonToSignInState(data: Record<string, unknown>): SignInState {
  const workspace = parseWorkspaceContextFromBffMePayload(data);
  return {
    customerInfo: readArray(data, 'CustomerInfo').map(mapCustomerInfoRow),
    userInfo: mapUserInfo(data.UserInfo),
    selectedWorkspaceId: workspace.selectedWorkspaceId,
    workspaceRoleId: workspace.workspaceRoleId,
    workspaceSubscription: workspace.workspaceSubscription,
    isWorkspaceSuperAdmin: workspace.isWorkspaceSuperAdmin,
    isAuthed: Boolean(data.IsAuthed),
    isVerified: Boolean(data.IsVerified),
    hasAccount: Boolean(data.HasAccount),
    currentCustomerID: Number(data.CurrentCustomerID ?? 0),
    isLoading: false,
  };
}

const emptySignInState = (isLoading: boolean): SignInState => ({
  customerInfo: [],
  userInfo: null,
  selectedWorkspaceId: null,
  workspaceRoleId: 0,
  workspaceSubscription: null,
  isWorkspaceSuperAdmin: false,
  isAuthed: false,
  isVerified: false,
  hasAccount: false,
  currentCustomerID: 0,
  isLoading,
});

export const SignInStateProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthChecked, isAuthenticated, login } = useAuth();
  const [signInState, setSignInState] = useState<SignInState>(emptySignInState(true));
  const reauthRedirectIssuedRef = useRef(false);

  const reloadWorkspaceForCustomer = useCallback(
    async (customerId: number, _customers: SignInState['customerInfo']) => {
      try {
        const resolved = await fetchWorkspaceSubscriptionForCustomer(customerId);
        setSignInState((s) => ({
          ...s,
          selectedWorkspaceId: resolved.selectedWorkspaceId,
          workspaceRoleId: resolved.workspaceRoleId,
          workspaceSubscription: resolved.workspaceSubscription,
          isWorkspaceSuperAdmin: resolved.isWorkspaceSuperAdmin,
        }));
      }
      catch {
        setSignInState((s) => ({
          ...s,
          selectedWorkspaceId: null,
          workspaceRoleId: 0,
          workspaceSubscription: null,
          isWorkspaceSuperAdmin: false,
        }));
      }
    },
    []
  );

  useEffect(() => {
    if (!isAuthChecked)
      return;

    if (!isAuthenticated) {
      setSignInState(emptySignInState(false));
      return;
    }

    const controller = new AbortController();
    const loadMe = async () => {
      setSignInState((s) => ({ ...s, isLoading: true }));
      try {
        const response = await fetch('/bff/api/me', {
          method: 'GET',
          credentials: 'include',
          headers: bffRequestHeaders(0),
          signal: controller.signal,
        });
        // A 401 here means the BFF cookie is still present but the upstream access token expired and
        // could not be refreshed. Re-authenticate (silent when the IdP session is alive) instead of
        // rendering the empty state, which would show a misleading "no workspace access" screen.
        if (response.status === 401) {
          if (!reauthRedirectIssuedRef.current) {
            reauthRedirectIssuedRef.current = true;
            login();
          }
          return;
        }
        if (!response.ok) {
          setSignInState(emptySignInState(false));
          return;
        }
        const data = (await response.json()) as Record<string, unknown>;
        const next = mapBffMeJsonToSignInState(data);
        setSignInState(next);
      }
      catch {
        setSignInState(emptySignInState(false));
      }
    };

    void loadMe();
    return () => controller.abort();
  }, [isAuthChecked, isAuthenticated, login]);

  const contextValue = useMemo(
    () => ({ signInState, setSignInState, reloadWorkspaceForCustomer }),
    [signInState, reloadWorkspaceForCustomer]
  );

  return (
    <SignInStateContext.Provider value={contextValue}>
      {children}
    </SignInStateContext.Provider>
  );
};

export const useSignInState = (): SignInStateContextType => {
  const context = useContext(SignInStateContext);
  if (!context) {
    throw new Error('useSignInState must be used within a SignInStateProvider');
  }
  return context;
};
